import React, { useContext, useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
  keyframes,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import { OrderContext } from "../contexts/OrderContext";
import axios from "axios";
import { downloadJobCardsByKarigar } from "../utils/jobCardPdf";

// Filled-in template served from public/templates. It documents every column,
// so keep the column order here and in the file in step with COLUMNS below.
const TEMPLATE_FILE = "Exhibition_Bulk_Order_Template.xlsx";

// Column order of the exhibition CSV template. The file is read positionally,
// so the columns must stay in this order.
const COLUMNS = {
  TAGNO: 0,
  IMAGE: 1,
  CUSTOMER_DELIVERY_DATE: 2,
  KARIGAR_DELIVERY_DATE: 3,
  ORDER_NUMBER: 4,
  STATUS: 5,
  ITEM_TYPE: 6,
  DESIGN: 7,
  KARIGAR: 8,
  PURITY: 9,
  PCS: 10,
  GROSS: 11,
  NET: 12,
  COLOR: 13, // not stored — no column on the orders table
  SCREW: 14,
  SIZE: 15,
  BRAND: 16, // not stored — no column on the orders table
  CUSTOMER: 17,
  IMAGE_PATH: 18,
};

const ExhibitionOrdersCreation = () => {
  const navigate = useNavigate();

  // Context and Local storage data
  const { storeData, setUpdateOrderStatusToggle } = useContext(OrderContext);
  const {
    mfrstore = [],
    allstatus = [],
    storeInfo = [],
    purities = [],
    itemtypes = [],
    designs = [],
    karigars = [],
    screwTypes = [],
  } = storeData;
  const url_prefix = storeInfo?.[0]?.url_prefix || "";
  const userRole = localStorage.getItem("userRole");
  const token = localStorage.getItem("authToken");

  const [existingDesignsArr, setExistingDesignsArr] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [ordersCreatedCount, setOrdersCreatedCount] = useState(0);
  const [errorOrdersCount, setErrorOrdersCount] = useState(0);
  const [loadingOrdersCount, setLoadingOrdersCount] = useState(0);
  const [loadingErrorOrdersCount, setLoadingErrorOrdersCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCreatingBulkOrder, setIsCreatingBulkOrder] = useState(false);
  const [isLoadingOverlay, setIsLoadingOverlay] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("Creating bulk orders");
  const [errorOrdersArr, setErrorOrdersArr] = useState([]);
  const [jobCardsCount, setJobCardsCount] = useState(0);
  const [jobCardFiles, setJobCardFiles] = useState([]);
  const [showSuccessBackdrop, setShowSuccessBackdrop] = useState(false);
  const [showErrorBackdrop, setShowErrorBackdrop] = useState(false);

  // Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Observe excel file input
  const fileInputRef = useRef();

  // For store name dropdown
  const [formData, setFormData] = useState({ storeName: null });

  // For store name dropdown options
  const mapStoreOptions = (data = []) =>
    data.map((item) => ({ id: item.id, name: item.name }));

  // Store name dropdown change handler
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Manufacturer ID
  let mfrId = "";
  if (
    userRole === "Customer" ||
    userRole === "SEMIMFR" ||
    userRole === "SUPERADMIN" ||
    userRole === "COMFR"
  ) {
    mfrId = JSON.parse(localStorage.getItem("selectedMfr"))?.id || "";
  } else if (userRole === "MFR") {
    mfrId = localStorage.getItem("id") || "";
  }

  // Check if both store name and file are supplied
  useEffect(() => {
    setIsCreatingBulkOrder(!!(formData.storeName && excelFile));
  }, [formData.storeName, excelFile]);

  // Fetching designs data from design catalogue API
  const existingDesignsFetcher = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/getStockByStoreG/${mfrId}`
      );
      // A store with no 'G' stock answers 404 with a message object.
      setExistingDesignsArr(
        Array.isArray(response?.data) ? response.data : []
      );
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    existingDesignsFetcher();
  }, []);

  // Handle csv file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExcelFile(file);
    setFileName(file.name);
    setErrorMessage("");
  };

  // Create bulk exhibition orders by hitting the api one row at a time so a
  // bad row only loses its own order.
  const submitOrders = async (orders) => {
    let successCount = 0;
    let errorCount = 0;
    let jobCards = 0;
    const errorRows = [];
    const createdOrders = [];

    setIsLoadingOverlay(true);
    setLoadingLabel("Creating bulk exhibition orders");

    for (const { payload, jobCardMeta, rowNumber } of orders) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/orders/createbulkexhibitionorder`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const createdOrder = response?.data?.data;
        if (response?.data?.jobCard) jobCards++;

        createdOrders.push({
          ...jobCardMeta,
          storeorderno: createdOrder?.storeorderno || "",
          orderdate: createdOrder?.orderdate || payload.orderdate,
        });

        successCount++;
        setLoadingOrdersCount(successCount);
      } catch (error) {
        console.error(`Exhibition row ${rowNumber} failed`, error);
        errorCount++;
        errorRows.push(rowNumber);
        setLoadingErrorOrdersCount(errorCount);
      }
    }

    setOrdersCreatedCount(successCount);
    setErrorOrdersCount(errorCount);
    setErrorOrdersArr(errorRows);
    setJobCardsCount(jobCards);

    // Job card PDFs — one merged file per karigar for everything just created.
    let files = [];
    if (createdOrders.length) {
      setLoadingLabel("Generating job cards");
      try {
        const storeName =
          mfrstore.find((store) => store.id === formData.storeName)?.name || "";
        files = await downloadJobCardsByKarigar(createdOrders, storeName);
      } catch (error) {
        console.error("Job card PDF generation failed", error);
        setSnackbarSeverity("warning");
        setSnackbarMessage(
          "Orders were created but the job card PDFs could not be generated."
        );
        setOpenSnackbar(true);
      }
    }
    setJobCardFiles(files);

    setIsLoadingOverlay(false);

    if (successCount === 0) {
      setShowErrorBackdrop(true);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to create exhibition orders.");
    } else {
      setShowSuccessBackdrop(true);
      setSnackbarSeverity("success");
      setSnackbarMessage("Exhibition orders created successfully!");
    }
    setOpenSnackbar(true);
    setLoadingOrdersCount(0);
    setUpdateOrderStatusToggle((prev) => !prev);
  };

  // Submit csv and create bulk exhibition orders handler
  const handleSubmit = async () => {
    if (!formData.storeName || !excelFile) return;

    setErrorMessage("");
    setIsCreatingBulkOrder(false); // Disable the button immediately on click

    const cell = (row, index) => String(row?.[index] ?? "").trim();

    // Template dates are DD-MM-YYYY; DD/MM/YYYY and ISO are accepted too.
    const formatDate = (input) => {
      const value = String(input ?? "").trim();
      if (!value) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

      const [day, month, year] = value.split(/[-/.]/);
      if (!day || !month || !year) return null;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    };

    const matchByName = (list, name) => {
      const wanted = String(name ?? "").trim().toLowerCase();
      if (!wanted) return undefined;
      return list.find(
        (item) => String(item?.name ?? "").trim().toLowerCase() === wanted
      );
    };

    const toNumber = (value) => {
      const parsed = parseFloat(String(value ?? "").replace(/,/g, ""));
      return isNaN(parsed) ? "" : parsed;
    };

    const processRawData = (rawData) => {
      if (rawData.length <= 1) {
        setErrorMessage(
          "No data available in the uploaded file. Please choose a different file."
        );
        setIsCreatingBulkOrder(true);
        return [];
      }

      // Row 1 is the header; row numbers below are the ones shown in Excel.
      return rawData.slice(1).map((row, index) => {
        const tagno = cell(row, COLUMNS.TAGNO);
        const purityFromCsv = cell(row, COLUMNS.PURITY);

        // Tag no wins: when the piece is in the stock catalogue its design,
        // item type, karigar and net weight are taken from there, and the
        // CSV names are only used when the tag no is unknown.
        const stockRow = existingDesignsArr.find(
          (design) =>
            tagno &&
            (tagno === design.tagno_alpha || tagno === design.certificateno)
        );

        const csvKarigar = matchByName(karigars, cell(row, COLUMNS.KARIGAR));
        const karigarId = stockRow?.supplier || csvKarigar?.id || "";
        const karigarName =
          karigars.find((k) => String(k.id) === String(karigarId))?.name ||
          cell(row, COLUMNS.KARIGAR) ||
          "Unassigned";

        const itemTypeId =
          stockRow?.itemtype ||
          matchByName(itemtypes, cell(row, COLUMNS.ITEM_TYPE))?.id ||
          "";
        const designId =
          stockRow?.design ||
          matchByName(designs, cell(row, COLUMNS.DESIGN))?.id ||
          "";

        const purity =
          matchByName(purities, purityFromCsv) ||
          purities.find((p) => p.name === purityFromCsv.slice(0, 2));

        const grossWt = toNumber(cell(row, COLUMNS.GROSS));
        const netWt =
          toNumber(stockRow?.netwt) === ""
            ? toNumber(cell(row, COLUMNS.NET))
            : toNumber(stockRow?.netwt);

        const pcs = toNumber(cell(row, COLUMNS.PCS)) || 1;
        const size = cell(row, COLUMNS.SIZE);
        const imagePath = cell(row, COLUMNS.IMAGE_PATH);

        const payload = {
          selectedDesignId: designId,
          selectedItemTypeId: itemTypeId,
          selectedMetalTypeId: "",
          selectedPurityId: purity?.id || "",
          selectedScrewTypeId:
            matchByName(screwTypes, cell(row, COLUMNS.SCREW))?.id || "",
          selectedStatusId:
            matchByName(allstatus, cell(row, COLUMNS.STATUS))?.id || "",
          selectedStoreId: formData.storeName,
          selectedKarigarId: karigarId,
          selectedMFR: mfrId || "",
          selectedItemSize: size,
          selectedDesignNumber: tagno,
          orderdate: new Date().toISOString().split("T")[0],
          orderDeliveryDate: formatDate(cell(row, COLUMNS.CUSTOMER_DELIVERY_DATE)),
          karigarDeliveryDate: formatDate(
            cell(row, COLUMNS.KARIGAR_DELIVERY_DATE)
          ),
          pcs,
          grossWt,
          netWt,
          orderDesc: tagno,
          customername: cell(row, COLUMNS.CUSTOMER),
          storeorderno: cell(row, COLUMNS.ORDER_NUMBER),
          imageUrl: imagePath,
          imageBlobURLLink: `https://kumuduorderapp.blob.core.windows.net/${url_prefix}`,
          createJobCard: true,
        };

        // Everything the job card PDF needs that the API does not echo back.
        const jobCardMeta = {
          karigar_name: karigarName,
          itemtype_name:
            itemtypes.find((i) => String(i.id) === String(itemTypeId))?.name ||
            cell(row, COLUMNS.ITEM_TYPE),
          design_name:
            designs.find((d) => String(d.id) === String(designId))?.name ||
            cell(row, COLUMNS.DESIGN),
          purity: purity?.name || purityFromCsv,
          pcs,
          size_len: size,
          weight: grossWt,
          netwt: netWt,
          description: tagno,
          customer_name: cell(row, COLUMNS.CUSTOMER),
          customer_order_number: cell(row, COLUMNS.ORDER_NUMBER),
          karigar_delivery_date: payload.karigarDeliveryDate,
          images: imagePath ? [imagePath] : [],
        };

        return { payload, jobCardMeta, rowNumber: index + 2 };
      });
    };

    // Read and parse csv file
    const reader = new FileReader();
    reader.onload = async (event) => {
      Papa.parse(event.target.result, {
        header: false,
        skipEmptyLines: true,
        complete: async (result) => {
          const rawData = result.data.filter(
            (row) =>
              Array.isArray(row) &&
              row.some((c) => c !== null && c !== undefined && String(c).trim())
          );

          const structuredOrders = processRawData(rawData);
          setTotalRowsCount(structuredOrders.length);
          setLoadingErrorOrdersCount(0);
          if (structuredOrders.length) await submitOrders(structuredOrders);
        },
      });
    };

    reader.readAsText(excelFile);
  };

  // Loading animation
  const dotsAnimation = keyframes`
  0%   { content: "."; }
  16%  { content: ".."; }
  32%  { content: "..."; }
  48%  { content: "...."; }
  64%  { content: "....."; }
  80%  { content: "....."; }
  100% { content: "."; }
`;

  // Reset the form if create more button is clicked
  const resetForm = () => {
    setFormData({ storeName: null });
    setExcelFile(null);
    setFileName("");
    setTotalRowsCount(0);
    setOrdersCreatedCount(0);
    setErrorOrdersCount(0);
    setLoadingOrdersCount(0);
    setLoadingErrorOrdersCount(0);
    setErrorMessage("");
    setErrorOrdersArr([]);
    setJobCardsCount(0);
    setJobCardFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  return (
    <Container maxWidth="lg" className="container" sx={{ py: 4 }}>
      {/* Heading and create one order button */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <h2 className="new-order-heading">Create Bulk Exhibition Orders</h2>
          <p className="new-order-note">
            Upload the exhibition template to create the orders and their job
            cards in one go.
            <br />
            Fields marked with <span style={{ color: "red" }}>*</span> are
            mandatory.
          </p>
        </Box>
        <Box
          onClick={() => navigate("/order")}
          style={{
            backgroundColor: "#0D7377",
            height: "40px",
            width: "150px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Create one order
        </Box>
      </Box>

      {/* Select store dropdown */}
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          options={mapStoreOptions(mfrstore)}
          getOptionLabel={(option) => option.name}
          value={
            formData.storeName
              ? {
                  id: formData.storeName,
                  name:
                    mfrstore.find((mfr) => mfr.id === formData.storeName)
                      ?.name || "",
                }
              : null
          }
          onChange={(e, newValue) =>
            handleFieldChange("storeName", newValue?.id || null)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <span>
                  Store Name <span style={{ color: "red" }}>*</span>{" "}
                  <span style={{ opacity: "0.5" }}>(Step - 1)</span>
                </span>
              }
              fullWidth
            />
          )}
        />
      </Box>

      {/* Download the template that explains every column */}
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Not sure what goes in which column? Download the template — it lists
          every column, what to fill in it and where it shows up in the app.
        </Typography>
        <Button
          component="a"
          href={`${process.env.PUBLIC_URL}/templates/${TEMPLATE_FILE}`}
          download={TEMPLATE_FILE}
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ whiteSpace: "nowrap" }}
        >
          Download template
        </Button>
      </Box>

      {/* Exhibition csv upload */}
      <Box sx={{ mb: 3 }}>
        <label
          htmlFor="exhibition-file-upload"
          style={{
            border: "2px dashed #0A5C60",
            borderRadius: "12px",
            padding: "30px",
            textAlign: "center",
            backgroundColor: excelFile ? "#e0e0e0" : "#f4f3f8",
            color: "#3e3e3e",
            cursor: excelFile ? "not-allowed" : "pointer",
            display: "block",
            pointerEvents: excelFile ? "none" : "auto",
            opacity: excelFile ? 0.6 : 1,
          }}
        >
          <input
            type="file"
            id="exhibition-file-upload"
            ref={fileInputRef}
            accept=".csv"
            hidden
            onChange={handleFileUpload}
          />
          <Typography component="span" fontWeight={600} color="primary">
            {!excelFile && <span style={{ color: "red" }}>*</span>} Click here
          </Typography>{" "}
          to upload csv file <span style={{ opacity: "0.5" }}>(Step - 2)</span>
        </label>
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, opacity: 0.7 }}
        >
          Expected columns, in this order: Tagno, Image, Customer Delivery Date,
          Karigar Delivery Date, Order Number, Status, Item Type, Design,
          Karigar, Purity, Pcs, Gross, Net, Color, Screw, Size, Brand, Customer,
          Image Path. Fill your data into the "Ready To Fill" sheet of the
          template, then save it as CSV (File &gt; Save As &gt; CSV UTF-8) and
          upload it here.
        </Typography>
        {excelFile && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ color: "green" }}>
              ✓ Uploaded <span style={{ fontStyle: "italic" }}>{fileName}</span>
            </Typography>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={() => {
                setExcelFile(null);
                setFileName("");
                if (fileInputRef.current) fileInputRef.current.value = null;
              }}
              sx={{ mt: 1, bgcolor: "rgba(224, 206, 206, 1)" }}
            >
              Remove file
            </Button>
          </Box>
        )}
      </Box>

      {/* Submit button */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isCreatingBulkOrder}
        >
          Create bulk exhibition orders
        </Button>
      </Box>

      {/* Error message */}
      {errorMessage && (
        <Typography
          variant="caption"
          sx={{ color: "red", mt: 2, fontStyle: "italic" }}
          dangerouslySetInnerHTML={{ __html: errorMessage }}
        />
      )}

      {/* Circular progress overlay while creating bulk orders */}
      {isLoadingOverlay && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 2000,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={4}
            width="100%"
            height="100%"
          >
            <CircularProgress size={60} sx={{ color: "#fff" }} />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "white" }}>
                {loadingLabel}
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: "1.5em",
                    textAlign: "left",
                    "::after": {
                      content: '""',
                      display: "inline-block",
                      whiteSpace: "pre",
                      animation: `${dotsAnimation} 5s steps(5, end) infinite`,
                    },
                  }}
                />
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "white" }}>
                Current progress: {`${loadingOrdersCount} / ${totalRowsCount}`}
              </Typography>
              {loadingErrorOrdersCount > 0 && (
                <Typography
                  sx={{ fontSize: 16, fontWeight: 600, color: "white" }}
                >
                  Order{loadingErrorOrdersCount > 1 ? "s" : ""} failed to
                  process: {loadingErrorOrdersCount}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Show success message or error in csv rows after bulk order creation */}
      <Dialog
        open={showSuccessBackdrop}
        onClose={() => setShowSuccessBackdrop(false)}
      >
        <DialogTitle>
          {ordersCreatedCount} exhibition order
          {ordersCreatedCount === 1 ? " was" : "s were"} created successfully
        </DialogTitle>

        <DialogTitle sx={{ pt: 0, fontSize: 16 }}>
          {jobCardsCount} job card{jobCardsCount === 1 ? "" : "s"} created
          {jobCardFiles.length > 0 && (
            <>
              {" "}
              — downloaded {jobCardFiles.length} PDF
              {jobCardFiles.length === 1 ? "" : "s"} (
              {jobCardFiles.join(", ")})
            </>
          )}
        </DialogTitle>

        {/* Error in csv rows */}
        {errorOrdersCount > 0 && (
          <DialogTitle sx={{ pt: 0, fontSize: 16 }}>
            <span style={{ color: "#ff6961" }}>{errorOrdersCount} </span> order
            {errorOrdersCount > 1 ? "s" : ""}{" "}
            {errorOrdersCount > 1 ? "were" : "was"} not created (Refer rows in
            csv: {errorOrdersArr.join(", ")})
          </DialogTitle>
        )}

        {/* Dialog box */}
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            flexDirection: "column",
          }}
        >
          <Button
            onClick={() => {
              setShowSuccessBackdrop(false);
              resetForm();
            }}
            sx={{ color: "green" }}
          >
            Create More
          </Button>
          <Button onClick={() => navigate("/home")}>Go Home</Button>
        </DialogActions>
      </Dialog>

      {/* Show error message for failed order creation */}
      <Dialog
        open={showErrorBackdrop}
        onClose={() => setShowErrorBackdrop(false)}
      >
        <DialogTitle>
          An unexpected error occured while creating the exhibition orders.
          Please try again.
        </DialogTitle>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            flexDirection: "column",
          }}
        >
          <Button
            onClick={() => {
              setShowErrorBackdrop(false);
              setIsCreatingBulkOrder(true);
            }}
            sx={{ color: "blue" }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "100px !important", // force override snackbar position
          },
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExhibitionOrdersCreation;
